import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Card, Table, Button, Space, TreeSelect, Input, Tag, message, DatePicker,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { clientApi, groupApi } from '../services/api';

export default function DeliveriesQueryPage() {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [clientId, setClientId] = useState('');
  const [range, setRange] = useState(null);
  const [stockCode, setStockCode] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clientNames, setClientNames] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    groupApi.list().then(r => { if (r.success) setGroups(r.data || []); });
  }, []);

  const groupTreeOptions = useMemo(() => {
    const map = {};
    const roots = [];
    groups.forEach(g => { map[g.id] = { ...g, children: undefined }; });
    groups.forEach(g => {
      if (g.parent_id && map[g.parent_id]) {
        const p = map[g.parent_id];
        (p.children || (p.children = [])).push(map[g.id]);
      } else roots.push(map[g.id]);
    });
    const to = nodes => nodes.map(g => ({
      value: g.id, title: g.name,
      ...(g.children && g.children.length ? { children: to(g.children) } : {}),
    }));
    return to(roots);
  }, [groups]);

  const doQuery = async () => {
    setLoading(true);
    try {
      const params = {};
      // 优先客户ID，否则按分组
      if (clientId && clientId.trim()) params.client_id = clientId.trim();
      else if (groupId) params.group_id = groupId;
      if (range && range[0]) params.from = range[0].format('YYYY-MM-DD');
      if (range && range[1]) params.to = range[1].format('YYYY-MM-DD');
      if (stockCode) params.stock_code = stockCode.trim();
      const r = await clientApi.getDeliveries(params);
      if (r.success) {
        setRows((r.data && r.data.deliveries) || []);
        setSummary((r.data && r.data.summary) || null);
        setClientNames((r.data && r.data.client_names) || {});
      } else message.error(r.message || '查询失败');
    } catch { message.error('查询失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { doQuery(); }, []);

  const sumItem = (label, val, color = '#cf1322') => (
    <span style={{ marginRight: 20 }}>{label}: <b style={{ color }}>{(val || 0).toFixed(2)}</b></span>
  );

  const columns = [
    { title: '客户', dataIndex: 'client_id', width: 110, render: (v, r) => <span>{clientNames[v] || v}<br /><code style={{ fontSize: 10, color: '#bbb' }}>{v}</code></span> },
    { title: '成交日期', dataIndex: 'trade_date', width: 100 },
    { title: '成交时间', dataIndex: 'trade_time_str', width: 90 },
    { title: '代码', dataIndex: 'stock_code', width: 80 },
    { title: '名称', dataIndex: 'stock_name', width: 90 },
    { title: '操作', dataIndex: 'direction', width: 60, render: v => v === 'buy' ? <Tag color="#cf1322">买入</Tag> : v === 'sell' ? <Tag color="#3f8600">卖出</Tag> : (v || '-') },
    { title: '数量', dataIndex: 'quantity', width: 70, align: 'right' },
    { title: '价格', dataIndex: 'price', width: 80, align: 'right', render: v => v ? v.toFixed(3) : '-' },
    { title: '成交金额', dataIndex: 'amount', width: 100, align: 'right', render: v => v ? v.toFixed(2) : '-' },
    { title: '余额', dataIndex: 'balance', width: 100, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
    { title: '发生金额', dataIndex: 'real_amount', width: 110, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
    { title: '印花税', dataIndex: 'stamping_tax', width: 90, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
    { title: '手续费', dataIndex: 'commission', width: 90, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
    { title: '过户费', dataIndex: 'transfer_fee', width: 90, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
    { title: '净佣金', dataIndex: 'net_commission', width: 90, align: 'right', render: v => (v != null && v !== 0) ? v.toFixed(2) : '-' },
    { title: '合同编号', dataIndex: 'contract_no', width: 130 },
    { title: '市场', dataIndex: 'market', width: 90 },
  ];

  return (
    <Card title="交割单总查询" size="small"
      extra={<Button size="small" icon={<ReloadOutlined />} onClick={doQuery}>查询</Button>}>
      <Space wrap style={{ marginBottom: 12 }}>
        <TreeSelect
          style={{ width: 220 }} placeholder="选择分组（含子分组）"
          treeData={groupTreeOptions} allowClear treeDefaultExpandAll
          value={groupId} onChange={setGroupId}
        />
        <Input.Search style={{ width: 180 }} placeholder="客户ID（不填则按分组）" value={clientId}
          onChange={e => setClientId(e.target.value.trim())} onPressEnter={doQuery} allowClear />
        <DatePicker.RangePicker
          value={range} onChange={setRange}
          ranges={{ 近一月: [dayjs().subtract(1, 'month'), dayjs()], 近三月: [dayjs().subtract(3, 'month'), dayjs()], 近一年: [dayjs().subtract(1, 'year'), dayjs()] }}
          placeholder={['起始日期', '结束日期']}
        />
        <Input style={{ width: 130 }} placeholder="股票代码" value={stockCode}
          onChange={e => setStockCode(e.target.value.trim())} onPressEnter={doQuery} />
        <Button type="primary" icon={<SearchOutlined />} onClick={doQuery}>查询</Button>
      </Space>

      <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 14 }}>
        <span style={{ marginRight: 20 }}>笔数: <b style={{ color: '#2980b9' }}>{(summary?.buy_count || 0) + (summary?.sell_count || 0)}</b>
          <span style={{ color: '#999', fontSize: 12 }}>（买 {summary?.buy_count || 0} / 卖 {summary?.sell_count || 0}）</span>
        </span>
        {sumItem('手续费合计', summary?.commission)}
        {sumItem('过户费合计', summary?.transfer_fee)}
        {sumItem('印花税合计', summary?.stamping_tax)}
        {sumItem('净佣金合计', summary?.net_commission)}
        {sumItem('成交金额合计', summary?.amount, '#2980b9')}
        {sumItem('发生金额合计', summary?.real_amount)}
      </div>

      <Table dataSource={rows} rowKey="id" size="small" loading={loading} bordered
        columns={columns} scroll={{ x: 1600 }} pagination={{ pageSize: 10, showSizeChanger: false, showTotal: t => `共 ${t} 条` }} />
    </Card>
  );
}
